import * as moment from 'moment'

import { BasicParameter, BasicParameterSize } from '../../common/parameters/extensions'
import { LocalizationHelper } from '../../../helpers'
import { OpLocation } from '../../../models/core'
import { OpCreditInfo, OpCurrentCreditInfo, OpEmergencyCreditInfo } from '../../../models/custom'


export const SERVICE_NAME: string = 'IMRCashierPanelLocationDetailsService'

export class CashierPanelLocationDetailsService {

    // Consts

    private static get PROPERTY_PRECISION(): number { return 2 }

    // Consts End


    // Public Methods

    public getCurrentCreditParameters(location: OpLocation): BasicParameter[] {
        const currentCreditParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CurrentCredit),
            size: BasicParameterSize.Large
        }

        const balanceOwedParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BalanceOwed),
            size: BasicParameterSize.Small
        }

        const lastCreditUpdateParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.LastCreditUpdate),
            size: BasicParameterSize.Small
        }

        const balanceFromDeviceParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BalanceFromDevice),
            size: BasicParameterSize.Small
        }

        const accountValidityTimeParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AccountValidityTime),
            size: BasicParameterSize.Small
        }

        const valveStateParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ValveState),
            size: BasicParameterSize.Small
        }

        if (location != null && location.consumer != null && location.consumer.creditInfo != null && location.consumer.creditInfo.currentCreditInfo != null) {
            const parameters: BasicParameter[] = []
            const creditInfo: OpCreditInfo = location.consumer.creditInfo
            const notAvailableAbbr = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotAvailableAbbr)
            const currentCreditInfo: OpCurrentCreditInfo = creditInfo.currentCreditInfo
            const currencySymbol: string = creditInfo.currency != null ? creditInfo.currency.toString() : notAvailableAbbr
            const currentCredit: string = currentCreditInfo.currentCredit != null ? currentCreditInfo.currentCredit.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const balanceOwed: string = currentCreditInfo.balanceOwed != null ? currentCreditInfo.balanceOwed.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const lastCreditUpdate: string = currentCreditInfo.lastCreditUpdate != null ? moment(currentCreditInfo.lastCreditUpdate).format('DD/MM/YYYY hh:mm:ss') : notAvailableAbbr

            currentCreditParameter.value = currentCredit
            currentCreditParameter.unit = currencySymbol
            currentCreditParameter.color = currentCreditInfo.currentCreditColorHex

            balanceOwedParameter.value = balanceOwed
            balanceOwedParameter.unit = currencySymbol

            lastCreditUpdateParameter.value = lastCreditUpdate

            parameters.push(
                currentCreditParameter,
                balanceOwedParameter,
                lastCreditUpdateParameter
            )

            if (currentCreditInfo.valveState != null) {
                const balanceFromDevice: string = currentCreditInfo.balanceFromDevice != null ? currentCreditInfo.balanceFromDevice.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
                const accountValidityTime: string = currentCreditInfo.accountValidityTime != null ? moment(currentCreditInfo.accountValidityTime).format('DD/MM/YYYY') : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Unlimited)

                balanceFromDeviceParameter.value = balanceFromDevice
                balanceFromDeviceParameter.unit = currencySymbol
                balanceFromDeviceParameter.color = currentCreditInfo.balanceFromDeviceColorHex

                accountValidityTimeParameter.value = accountValidityTime
                accountValidityTimeParameter.color = currentCreditInfo.accountValidityTimeColorHex

                valveStateParameter.value = currentCreditInfo.valveState.toString()
                valveStateParameter.color = currentCreditInfo.valveStateColorHex

                parameters.push(
                    balanceFromDeviceParameter,
                    accountValidityTimeParameter,
                    valveStateParameter
                )
            }

            return parameters
        }

        return [
            currentCreditParameter,
            balanceOwedParameter,
            lastCreditUpdateParameter,
            balanceFromDeviceParameter,
            accountValidityTimeParameter,
            valveStateParameter
        ]
    }

    public getEmergencyCreditParameters(location: OpLocation): BasicParameter[] {
        const emergencyCreditParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EmergencyCredit),
            size: BasicParameterSize.Large
        }

        const availableAtParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AvailableAt),
            size: BasicParameterSize.Small
        }

        const creditTopupParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CreditTopup),
            size: BasicParameterSize.Small
        }

        const repaymentParameter: BasicParameter = {
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Repayment),
            size: BasicParameterSize.Small
        }

        if (location != null &&
            location.consumer != null &&
            location.consumer.creditInfo != null &&
            location.consumer.creditInfo.emergencyCreditInfo != null &&
            location.consumer.creditInfo.currentCreditInfo != null &&
            location.consumer.creditInfo.currentCreditInfo.valveState != null
        ) {
            const creditInfo: OpCreditInfo = location.consumer.creditInfo
            const emergencyCreditInfo: OpEmergencyCreditInfo = creditInfo.emergencyCreditInfo
            const notAvailableAbbr = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotAvailableAbbr)
            const currencySymbol: string = creditInfo.currency != null ? creditInfo.currency.toString() : notAvailableAbbr

            const userEC: string = emergencyCreditInfo.usedEC != null ? emergencyCreditInfo.usedEC.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const consumerTypeEcLimit: string = emergencyCreditInfo.consumerTypeEcLimit != null ? emergencyCreditInfo.consumerTypeEcLimit.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const emergencyCreditAvailableAt: string = emergencyCreditInfo.emergencyCreditAvailableAt != null ? emergencyCreditInfo.emergencyCreditAvailableAt.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const emergencyCreditTopupValue: string = emergencyCreditInfo.emergencyCreditTopupValue != null ? emergencyCreditInfo.emergencyCreditAvailableAt.toFixed(CashierPanelLocationDetailsService.PROPERTY_PRECISION) : notAvailableAbbr
            const ecRepaymentPercentage: string = emergencyCreditInfo.ecRepaymentPercentage != null ? emergencyCreditInfo.ecRepaymentPercentage : notAvailableAbbr

            emergencyCreditParameter.value = `${userEC}/${consumerTypeEcLimit}`
            emergencyCreditParameter.unit = currencySymbol
            emergencyCreditParameter.color = emergencyCreditInfo.emergencyCreditInfoColorHex

            availableAtParameter.value = emergencyCreditAvailableAt
            availableAtParameter.unit = currencySymbol

            creditTopupParameter.value = emergencyCreditTopupValue
            creditTopupParameter.unit = currencySymbol

            repaymentParameter.value = `${ecRepaymentPercentage}%`
        }

        return [
            emergencyCreditParameter,
            availableAtParameter,
            creditTopupParameter,
            repaymentParameter,
        ]
    }

    // Public Methods End
}
